/**
 * Upsell Dialog Web Component
 * Displays a modal after add-to-cart showing gift progress and upsell products
 * with quantity controls and checkout CTA.
 */
class UpsellDialog extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      activeClass: 'active',
      bodyOpenClass: 'upsell-dialog-open',
      cardSelectedClass: 'upsell-dialog__upsell-card--selected',
      minusActiveClass: 'active'
    };

    // DOM elements
    this.overlay = this.querySelector('.upsell-dialog__overlay');
    this.container = this.querySelector('.upsell-dialog__container');
    this.closeBtn = this.querySelector('.upsell-dialog__close');
    this.primaryBtn = this.querySelector('[data-action="checkout"]');
    this.secondaryBtn = this.querySelector('[data-action="decline"]');

    // Gift progress elements
    this.giftSection = this.querySelector('[data-gift-section]');
    this.giftImageContainer = this.querySelector('[data-gift-image-container]');
    this.giftStatus = this.querySelector('[data-gift-status]');
    this.giftBar = this.querySelector('[data-gift-bar]');
    this.giftGoal = this.querySelector('[data-gift-goal]');
    this.giftDescription = this.querySelector('[data-gift-description]');

    // Upsell elements
    this.upsellSection = this.querySelector('[data-upsell-section]');
    this.upsellCardsContainer = this.querySelector('[data-upsell-cards]');
    this.upsellDiscountEl = this.querySelector('[data-upsell-discount]');

    // Footer price elements
    this.totalCompareEl = this.querySelector('[data-total-compare]');
    this.totalFinalEl = this.querySelector('[data-total-final]');
    this.primaryBtnLabel = this.querySelector('[data-primary-button-label]');
    this.secondaryBtnLabel = this.querySelector('[data-secondary-button-label]');

    // Callback functions
    this.onCheckoutCallback = null;
    this.onDeclineCallback = null;

    // State
    this.mainProduct = null;
    this.upsellProducts = [];
    this.giftConfig = null;
    this.isSubscription = true;
    this.upsellQuantities = {}; // { productId: quantity }
    this.previousActiveElement = null;
    this.confirmationSyncTimeout = null;

    this.bindEvents();
  }

  connectedCallback() {
    // Support Shopify theme editor
    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }
  }

  disconnectedCallback() {
    if (Shopify && Shopify.designMode) {
      document.removeEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }
  }

  onSectionSelect(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      // Show with demo data in theme editor
      this.show({
        mainProduct: { price: 3900, title: 'Demo Product' },
        upsellProducts: [],
        giftConfig: { threshold: 7500 },
        isSubscription: true
      });
    }
  }

  onSectionDeselect(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      this.hide();
    }
  }

  bindEvents() {
    // Close button click
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.decline());
    }

    // Overlay click (close when clicking outside container)
    if (this.overlay) {
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) {
          this.decline();
        }
      });
    }

    // Primary CTA click (Checkout with upsells)
    if (this.primaryBtn) {
      this.primaryBtn.addEventListener('click', () => {
        const callback = this.onCheckoutCallback;
        const selectedUpsells = this.getSelectedUpsells();
        this.trackEngagement('upsell_dialog_add_to_cart', {
          engagementType: 'add_to_cart_click',
          clickText: (this.primaryBtnLabel && this.primaryBtnLabel.textContent.trim()) || 'Add to cart',
          productTitle: this.mainProduct && this.mainProduct.title
        });
        this.hide();
        if (typeof callback === 'function') {
          callback(selectedUpsells);
        }
      });
    }

    // Secondary CTA click (No thanks)
    if (this.secondaryBtn) {
      this.secondaryBtn.addEventListener('click', () => {
        this.trackEngagement('upsell_dialog_decline', {
          engagementType: 'generic_click',
          clickText: (this.secondaryBtnLabel && this.secondaryBtnLabel.textContent.trim()) || 'No thanks, continue'
        });
        this.decline();
      });
    }

    // Escape key to close
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains(this.classes.activeClass)) {
        this.decline();
      }
    });
  }

  /**
   * Fire a GA4 event for a dialog interaction. Each interaction gets its own
   * descriptive event name; engagement_type maps to the site taxonomy
   * (generic_click / add_to_cart_click / …). Uses gtag when available, else
   * pushes to the GTM dataLayer. Params that don't apply (e.g. click_href on a
   * <button>, an absent product) are omitted.
   * @param {string} eventName - descriptive GA4 event name (e.g. 'upsell_dialog_add_to_cart')
   * @param {Object} [opts]
   * @param {string} [opts.engagementType='generic_click'] - site engagement_type taxonomy value
   * @param {string} [opts.clickText] - human-readable label of what was clicked
   * @param {string} [opts.productTitle] - product the interaction relates to
   */
  trackEngagement(eventName, { engagementType = 'generic_click', clickText, productTitle } = {}) {
    try {
      const sectionId = this.dataset.sectionId || (this.id || '').replace(/^upsell-dialog-/, '');
      const params = {
        engagement_type: engagementType,
        page_section: `sections--${sectionId}__upsell-dialog`
      };
      if (this.dataset.pageType) params.page_type = this.dataset.pageType;
      if (clickText) params.click_text = clickText;
      if (productTitle) params.product_title = productTitle;

      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: eventName }, params));
      }
      console.log('[Upsell-Dialog] GA4 event:', eventName, params);
    } catch (err) {
      console.warn('[Upsell-Dialog] trackEngagement failed:', err);
    }
  }

  /**
   * Decline and continue without upsells
   */
  decline() {
    const callback = this.onDeclineCallback;
    this.hide();
    if (typeof callback === 'function') {
      callback();
    }
  }

  /**
   * Show the dialog with product data
   * @param {Object} options - Configuration options
   * @param {Object} options.mainProduct - Main product being added to cart
   * @param {Array} options.upsellProducts - Array of upsell product objects
   * @param {Object} options.giftConfig - Gift threshold configuration
   * @param {boolean} options.isSubscription - Whether this is a subscription purchase
   * @param {Function} options.onCheckout - Callback when user clicks Checkout
   * @param {Function} options.onDecline - Callback when user declines
   */
  show(options = {}) {
    console.log('[Upsell-Dialog] Showing dialog with options:', options);

    this.clearConfirmationSyncTimeout();

    // Store data
    this.mainProduct = options.mainProduct || null;
    this.upsellProducts = options.upsellProducts || [];
    this.giftConfig = options.giftConfig || null;
    this.isSubscription = options.isSubscription !== false;
    this.upsellQuantities = {};

    // Store callbacks
    this.onCheckoutCallback = options.onCheckout || null;
    this.onDeclineCallback = options.onDecline || null;

    // Store current focus
    this.previousActiveElement = document.activeElement;

    // Render content
    this.renderGiftSection();
    this.renderUpsellCards();
    this.renderButtonLabels();

    // Preselect upsell product if index provided
    const preselectedIndex = options.preselectedIndex ?? -1;
    if (preselectedIndex >= 0 && preselectedIndex < this.upsellProducts.length) {
      const productToPreselect = this.upsellProducts[preselectedIndex];
      if (productToPreselect) {
        this.updateUpsellQuantity(String(productToPreselect.id), 1);
      }
    }

    this.calculateTotals();

    // Show dialog
    this.classList.add(this.classes.activeClass);

    // Prevent body scroll
    document.body.classList.add(this.classes.bodyOpenClass);

    // Set up focus trap
    this.trapFocus();

    // Announce to screen readers
    this.setAttribute('aria-hidden', 'false');

    // Dispatch event
    this.dispatchEvent(new CustomEvent('upsell-dialog:opened'));

    // Intelligems analytics: signal that the upsell pop-up was viewed.
    try {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: 'viewUpsellPopUp' });
    } catch (e) {
      console.warn('[Upsell-Dialog] igEvents push failed:', e);
    }

    // GA4 / GTM: signal that the upsell dialog was viewed.
    this.trackEngagement('upsell_dialog_view', {
      engagementType: 'view',
      clickText: 'Upsell dialog viewed',
      productTitle: this.mainProduct && this.mainProduct.title
    });

    this.confirmFooterPricesAfterOpen();
  }

  /**
   * Hide the dialog
   */
  hide() {
    console.log('[Upsell-Dialog] Hiding dialog');

    this.clearConfirmationSyncTimeout();

    // Hide dialog
    this.classList.remove(this.classes.activeClass);

    // Restore body scroll
    document.body.classList.remove(this.classes.bodyOpenClass);

    // Restore focus
    if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
      this.previousActiveElement.focus();
    }

    // Update accessibility
    this.setAttribute('aria-hidden', 'true');

    // Clear callbacks
    this.onCheckoutCallback = null;
    this.onDeclineCallback = null;

    // Dispatch event
    this.dispatchEvent(new CustomEvent('upsell-dialog:closed'));
  }

  /**
   * Render the gift progress section
   */
  renderGiftSection() {
    if (!this.giftConfig || !this.giftSection) return;

    const {
      threshold,
      giftProduct,
      statusIncomplete,
      statusComplete,
      badgeOriginalPrice,
      badgeSalePrice
    } = this.giftConfig;

    // Update goal display
    if (this.giftGoal) {
      this.giftGoal.textContent = '$' + (threshold / 100).toFixed(0);
    }

    // Substitute the live threshold into the gift description. The description
    // is a single global section setting, so it carries a {threshold} token
    // (e.g. "...with orders over {threshold}") that we fill in per-product here.
    if (this.giftDescription) {
      // Cache the raw template once — on first render it still holds the token.
      if (this.giftDescriptionTemplate == null) {
        this.giftDescriptionTemplate = this.giftDescription.innerHTML;
      }
      const thresholdText = Number.isFinite(threshold)
        ? '$' + (threshold / 100).toFixed(0)
        : '';
      this.giftDescription.innerHTML = this.giftDescriptionTemplate.replace(
        /\{threshold\}/g,
        thresholdText
      );
    }

    // Render gift image if provided
    if (this.giftImageContainer && giftProduct) {
      const badgeOriginal = badgeOriginalPrice || `$${((giftProduct.comparePrice || giftProduct.price) / 100).toFixed(2)}`;
      const badgeSale = badgeSalePrice || 'FREE';

      this.giftImageContainer.innerHTML = `
        <img
          src="${giftProduct.imageUrl || ''}"
          alt="${giftProduct.title || 'Free Gift'}"
          class="upsell-dialog__gift-image"
          loading="lazy"
          width="80"
          height="90"
        >
        <div class="upsell-dialog__gift-badge">
          <span class="upsell-dialog__gift-badge-original">${badgeOriginal}</span>
          <span class="upsell-dialog__gift-badge-sale">${badgeSale}</span>
        </div>
      `;
    }

    // Initial progress update
    this.updateGiftProgress();
  }

  /**
   * Apply configurable CTA labels from the dialog config.
   */
  renderButtonLabels() {
    const primaryText = this.giftConfig?.primaryButtonText || this.primaryBtnLabel?.dataset.defaultText;
    const secondaryText = this.giftConfig?.secondaryButtonText || this.secondaryBtnLabel?.dataset.defaultText;

    // if (this.primaryBtnLabel && primaryText) {
    //   this.primaryBtnLabel.textContent = primaryText;
    // }

    if (this.secondaryBtnLabel && secondaryText) {
      this.secondaryBtnLabel.textContent = secondaryText;
    }
  }

  /**
   * Update gift progress bar and status text
   */
  updateGiftProgress() {
    if (!this.giftConfig) return;

    const threshold = this.giftConfig.threshold || 7500;
    const statusIncomplete = this.giftConfig.statusIncomplete || 'You are {amount} away, keep going!';
    const statusComplete = this.giftConfig.statusComplete || "You've unlocked the free gift!";

    // Calculate current total
    const currentTotal = this.getGiftProgressTotal();
    const remaining = Math.max(threshold - currentTotal, 0);
    const percentage = Math.min((currentTotal / threshold) * 100, 100);
    const isComplete = currentTotal >= threshold;

    console.log('[existingMinicartCalc] gift progress values', {
      threshold,
      currentTotal,
      remaining,
      percentage,
      isComplete
    });

    // Update progress bar
    if (this.giftBar) {
      this.giftBar.style.width = percentage + '%';
      if (isComplete) {
        this.giftBar.classList.add('upsell-dialog__gift-bar-fill--complete');
      } else {
        this.giftBar.classList.remove('upsell-dialog__gift-bar-fill--complete');
      }
    }

    if (this.giftGoal) {
      this.giftGoal.textContent = '$' + (remaining / 100).toFixed(2).replace(/\.00$/, '');
    }

    // Update status text
    if (this.giftStatus) {
      if (isComplete) {
        this.giftStatus.textContent = statusComplete;
      } else {
        const remainingFormatted = '$' + (remaining / 100).toFixed(2);
        this.giftStatus.textContent = statusIncomplete.replace('{amount}', remainingFormatted);
      }
    }
  }

  /**
   * Read the current mini-cart subtotal rendered in the flyout footer.
   * @param {'sale'|'compare'} type - Which subtotal to read
   * @returns {number|null} Total in cents
   */
  getMiniCartSubtotal(type = 'sale') {
    const parentElementId = type === 'compare'
      ? 'cartTotalPrice'
      : 'label-subscription-price';
    const directValueParent = document.getElementById(parentElementId);

    if (directValueParent) {
      const childElements = directValueParent.children;
      const directValueElement = childElements.length > 1
        ? childElements[1]
        : directValueParent.querySelector('[aria-hidden="true"]');
      const directPriceText = directValueElement?.textContent?.trim() || '';
      const directCents = this.parseMoneyToCents(directPriceText);

      console.log('[existingMinicartCalc] mini-cart subtotal read from parent id', {
        type,
        parentElementId,
        childCount: childElements.length,
        usedSecondChild: childElements.length > 1,
        priceText: directPriceText,
        cents: directCents
      });

      return Number.isFinite(directCents) ? directCents : null;
    }

    const subtotalRow = document.querySelector(
      'aside#main-nav .firstday-cart__flyout-footer .firstday-cart__flyout-subtotal.original-price'
    );
    if (!subtotalRow) {
      console.log('[existingMinicartCalc] mini-cart subtotal row not found', { type, parentElementId });
      return null;
    }

    const amountContainer = subtotalRow.querySelector('.firstday-cart__flyout-subtotal-amount');
    if (!amountContainer) {
      console.log('[existingMinicartCalc] mini-cart amount container not found', { type, parentElementId });
      return null;
    }

    const selector = type === 'compare'
      ? '.mini-cart-original-price [aria-hidden="true"], .mini-cart-original-price'
      : '.mini-cart-price.sale [aria-hidden="true"], .mini-cart-price.sale';

    const matchedElement = amountContainer.querySelector(selector);
    const priceText = matchedElement?.textContent?.trim()
      || amountContainer.textContent?.trim()
      || '';
    const cents = this.parseMoneyToCents(priceText);

    console.log('[existingMinicartCalc] mini-cart subtotal read', {
      type,
      parentElementId,
      selector,
      matched: Boolean(matchedElement),
      priceText,
      cents
    });

    return Number.isFinite(cents) ? cents : null;
  }

  /**
   * Get gift progress total in cents, including the existing mini-cart subtotal.
   * This is used only for the gift progress UI, not the dialog CTA totals.
   * @returns {number}
   */
  getGiftProgressTotal() {
    const existingCartTotal = this.getMiniCartSubtotal('sale') || 0;
    const dialogTotal = this.getCurrentTotal();
    const total = existingCartTotal + dialogTotal;

    console.log('[existingMinicartCalc] gift progress total computed', {
      existingCartTotal,
      dialogTotal,
      total
    });

    return total;
  }

  /**
   * Get current total in cents (main product + upsells)
   */
  getCurrentTotal() {
    const liveSummaryTotal = this.getLiveSummarySubtotal('PBFCM-breakdown-subtotal-final');
    let total = liveSummaryTotal;
    let usedFallbackMainProduct = false;

    // Fall back to the dialog's local product data if the PDP subtotal isn't available.
    if (total === null) {
      total = 0;
      usedFallbackMainProduct = true;

      if (this.mainProduct) {
        const mainPrice = this.isSubscription
          ? (this.mainProduct.subscriptionPrice || this.mainProduct.price)
          : this.mainProduct.price;
        const mainQty = this.mainProduct.quantity || 1;
        total += mainPrice * mainQty;
      }
    }

    // Add upsell prices
    Object.keys(this.upsellQuantities).forEach(productId => {
      const qty = this.upsellQuantities[productId];
      if (qty > 0) {
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        if (product) {
          const price = this.isSubscription
            ? (product.subscriptionPrice || product.price)
            : (product.onetimePrice || product.price);
          total += price * qty;
        }
      }
    });

    console.log('[existingMinicartCalc] current total computed', {
      liveSummaryTotal,
      usedFallbackMainProduct,
      upsellQuantities: this.upsellQuantities,
      total
    });

    return total;
  }

  /**
   * Get current original total in cents (without discounts)
   */
  getOriginalTotal() {
    const liveSummaryCompareTotal = this.getLiveSummarySubtotal('PBFCM-breakdown-subtotal-compare');
    let total = liveSummaryCompareTotal;
    let usedFallbackMainProduct = false;

    // Fall back to the dialog's local product data if the PDP compare subtotal isn't available.
    if (total === null) {
      total = 0;
      usedFallbackMainProduct = true;

      if (this.mainProduct) {
        const mainPrice = this.mainProduct.comparePrice || this.mainProduct.price;
        const mainQty = this.mainProduct.quantity || 1;
        total += mainPrice * mainQty;
      }
    }

    // Add upsell original prices
    Object.keys(this.upsellQuantities).forEach(productId => {
      const qty = this.upsellQuantities[productId];
      if (qty > 0) {
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        if (product) {
          const price = product.comparePrice || product.price;
          total += price * qty;
        }
      }
    });

    console.log('[existingMinicartCalc] original total computed', {
      liveSummaryCompareTotal,
      usedFallbackMainProduct,
      upsellQuantities: this.upsellQuantities,
      total
    });

    return total;
  }

  /**
   * Get the unlocked gift's original price in cents.
   * @returns {number}
   */
  getGiftOriginalTotal() {
    if (!this.giftConfig) return 0;

    const threshold = this.giftConfig.threshold || 7500;
    const currentTotal = this.getCurrentTotal();

    if (currentTotal < threshold) {
      return 0;
    }

    const badgeOriginalCents = this.parseMoneyToCents(this.giftConfig.badgeOriginalPrice);
    if (Number.isFinite(badgeOriginalCents) && badgeOriginalCents > 0) {
      return badgeOriginalCents;
    }

    const giftProduct = this.giftConfig.giftProduct;
    if (!giftProduct) return 0;

    return giftProduct.comparePrice || giftProduct.price || 0;
  }

  /**
   * Read a subtotal value already rendered in the PDP summary.
   * @param {string} elementId - Summary subtotal element ID
   * @returns {number|null} Total in cents
   */
  getLiveSummarySubtotal(elementId) {
    const subtotalEl = document.getElementById(elementId);
    if (!subtotalEl) return null;

    const priceText = subtotalEl.querySelector('[aria-hidden="true"]')?.textContent || subtotalEl.textContent;
    const cents = this.parseMoneyToCents(priceText);

    return Number.isFinite(cents) ? cents : null;
  }

  /**
   * Get the rendered money string from a live PDP subtotal element.
   * @param {string} elementId - Summary subtotal element ID
   * @returns {string}
   */
  getLiveSummarySubtotalText(elementId) {
    const subtotalEl = document.getElementById(elementId);
    if (!subtotalEl) return '';

    return subtotalEl.querySelector('[aria-hidden="true"]')?.textContent?.trim()
      || subtotalEl.textContent?.trim()
      || '';
  }

  /**
   * Parse a money string like "$46.41" into cents.
   * @param {string} value - Money string
   * @returns {number|null} Amount in cents
   */
  parseMoneyToCents(value) {
    if (!value) return null;

    const normalized = String(value).replace(/[^0-9.-]/g, '');
    if (!normalized) return null;

    const amount = Number.parseFloat(normalized);
    if (!Number.isFinite(amount)) return null;

    return Math.round(amount * 100);
  }

  /**
   * Clear any pending confirmation sync timer.
   */
  clearConfirmationSyncTimeout() {
    if (this.confirmationSyncTimeout) {
      clearTimeout(this.confirmationSyncTimeout);
      this.confirmationSyncTimeout = null;
    }
  }

  /**
   * Log the source/footer price state and resync the footer totals.
   * @param {string} stage - Log stage label
   */
  logAndSyncFooterTotals(stage) {
    const sourceCompareText = this.getLiveSummarySubtotalText('PBFCM-breakdown-subtotal-compare');
    const sourceFinalText = this.getLiveSummarySubtotalText('PBFCM-breakdown-subtotal-final');
    const footerCompareText = this.totalCompareEl?.textContent?.trim() || '';
    const footerFinalText = this.totalFinalEl?.textContent?.trim() || '';

    console.log('[Confirmation-upsell-price]', stage, {
      source: {
        compare: sourceCompareText || null,
        final: sourceFinalText || null
      },
      footerBefore: {
        compare: footerCompareText || null,
        final: footerFinalText || null
      }
    });

    this.calculateTotals();

    console.log('[Confirmation-upsell-price]', `${stage}:after-calculate`, {
      footerAfter: {
        compare: this.totalCompareEl?.textContent?.trim() || null,
        final: this.totalFinalEl?.textContent?.trim() || null
      }
    });
  }

  /**
   * Reconfirm footer prices right after open and again after a short delay.
   */
  confirmFooterPricesAfterOpen() {
    this.logAndSyncFooterTotals('immediate');

    this.confirmationSyncTimeout = setTimeout(() => {
      this.logAndSyncFooterTotals('after-1ms');
      this.confirmationSyncTimeout = null;
    }, 1);
  }

  /**
   * Render upsell product cards
   */
  renderUpsellCards() {
    if (!this.upsellCardsContainer) return;

    if (!this.upsellProducts || this.upsellProducts.length === 0) {
      this.upsellCardsContainer.innerHTML = '';
      if (this.upsellSection) {
        this.upsellSection.style.display = 'none';
      }
      return;
    }

    if (this.upsellSection) {
      this.upsellSection.style.display = '';
    }

    const cardsHTML = this.upsellProducts.map(product => this.renderUpsellCard(product)).join('');
    this.upsellCardsContainer.innerHTML = cardsHTML;

    // Bind quantity button events
    this.upsellCardsContainer.querySelectorAll('.upsell-dialog__qty-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const productId = btn.dataset.productId;
        const action = btn.dataset.action;
        const delta = action === 'increase' ? 1 : -1;
        // Track each +/- click (real clicks only — not the programmatic preselect)
        // so we can see which upsell product titles are being engaged with.
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        this.trackEngagement(
          action === 'increase' ? 'upsell_dialog_quantity_increase' : 'upsell_dialog_quantity_decrease',
          {
            engagementType: 'generic_click',
            clickText: action === 'increase' ? 'Increase upsell quantity' : 'Decrease upsell quantity',
            productTitle: product && product.title
          }
        );
        this.updateUpsellQuantity(productId, delta);
      });
    });
  }

  /**
   * Render a single upsell card
   * @param {Object} product - Product object
   * @returns {string} HTML string
   */
  renderUpsellCard(product) {
    const price = this.isSubscription
      ? (product.subscriptionPrice || product.price)
      : (product.onetimePrice || product.price);
    const comparePrice = product.comparePrice || product.price;
    const quantity = this.upsellQuantities[product.id] || 0;
    const isSelected = quantity > 0;
    const maxQty = product.maxQuantity || 5;

    const priceFormatted = '$' + (price / 100).toFixed(2);
    const compareFormatted = '$' + (comparePrice / 100).toFixed(2);

    // Discount % badge shown on the product image (reflects the active
    // subscription/one-time price vs the compare-at price). Hidden when there's
    // no saving.
    const badgeEnabled = this.dataset.discountBadgeEnabled === 'true';
    const discountPct = comparePrice > 0 && comparePrice > price
      ? Math.round((comparePrice - price) / comparePrice * 100)
      : 0;
    const discountBadge = (badgeEnabled && discountPct > 0)
      ? `<span class="upsell-dialog__upsell-discount-badge" aria-hidden="true">${discountPct}%</span>`
      : '';

    const description = product.description
      ? `<p class="upsell-dialog__upsell-description">${product.description}</p>`
      : '';

    // Title: show the custom.short_name metafield, falling back to the full
    // product title whenever the short name is empty.
    const fullTitle = product.title || 'Product';
    const shortName = product.shortName || '';
    const displayTitle = shortName || fullTitle;

    // Optional per-card "more info" link → opens the shared product info modal
    // (window.FDInfoModal, rendered globally in theme.liquid). Gated on the
    // section setting (data-upsell-info-enabled) and the product having a handle.
    const infoEnabled = this.dataset.upsellInfoEnabled === 'true';
    const infoLabel = this.dataset.upsellInfoLabel || 'Learn More';
    const infoLink = (infoEnabled && product.handle)
      ? `<button type="button" class="upsell-dialog__upsell-info-link" data-info-modal-open="${product.handle}">${infoLabel}</button>`
      : '';

    const selectedClass = isSelected ? this.classes.cardSelectedClass : '';
    const minusActiveClass = quantity > 0 ? this.classes.minusActiveClass : '';
    const unlockHtml = this.renderUnlockAttachment(product.freeUnlock);
    const hasUnlockClass = unlockHtml ? 'upsell-dialog__upsell-card--has-unlock' : '';

    return `
      <div class="upsell-dialog__upsell-card ${selectedClass} ${hasUnlockClass}" data-product-id="${product.id}">
        <div class="upsell-dialog__upsell-product">
          ${product.imageUrl ? `
            <div class="upsell-dialog__upsell-image">
              ${discountBadge}
              <img
                src="${product.imageUrl}"
                alt="${product.title || ''}"
                class="upsell-dialog__upsell-img"
                loading="lazy"
                width="48"
                height="48"
              >
            </div>
          ` : ''}
          <div class="upsell-dialog__upsell-info">
            <h4 class="upsell-dialog__upsell-title">${displayTitle}</h4>
            ${description}
            ${infoLink}
          </div>
        </div>
        <div class="upsell-dialog__upsell-controls">
          <div class="upsell-dialog__upsell-price-box">
            <span class="upsell-dialog__upsell-price-compare">
              <span class="sr-only">Original price ${compareFormatted}, strikethrough</span>
              <span aria-hidden="true">${compareFormatted}</span>
            </span>
            <span class="upsell-dialog__upsell-price-final">
              <span class="sr-only">Sale price ${priceFormatted}</span>
              <span aria-hidden="true">${priceFormatted}</span>
            </span>
          </div>
          <div class="upsell-dialog__upsell-quantity" role="group" aria-label="Quantity for ${product.title || 'product'}">
            <button
              type="button"
              class="upsell-dialog__qty-btn upsell-dialog__qty-btn--minus ${minusActiveClass}"
              data-product-id="${product.id}"
              data-action="decrease"
              aria-label="Decrease quantity"
              ${quantity === 0 ? 'disabled' : ''}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <span class="upsell-dialog__qty-display" aria-live="polite" aria-atomic="true">${quantity}</span>
            <button
              type="button"
              class="upsell-dialog__qty-btn upsell-dialog__qty-btn--plus"
              data-product-id="${product.id}"
              data-action="increase"
              aria-label="Increase quantity"
              ${quantity >= maxQty ? 'disabled' : ''}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <input type="hidden"
          class="upsell-dialog__upsell-data"
          data-product-id="${product.id}"
          data-variant-id="${product.variantId}"
          data-price="${price}"
          data-compare-price="${comparePrice}"
          data-is-addon="${product.isAddon ? 'true' : 'false'}"
          data-title="${product.title || ''}"
          data-max="${maxQty}"
        >
        ${unlockHtml}
      </div>
    `;
  }

  /**
   * Render the free "unlocks" attachment beneath an upsell card.
   * Shows a separate product offered free with this item (no quantity stepper —
   * its quantity follows the trigger upsell and it's added to cart by the
   * controller). Returns '' when the product has no freeUnlock configured.
   * @param {Object|null} unlock - product.freeUnlock from the Liquid JSON
   * @returns {string} HTML string
   */
  renderUnlockAttachment(unlock) {
    if (!unlock || !unlock.variantId) return '';

    const compare = unlock.comparePrice || 0;
    const compareFormatted = '$' + (compare / 100).toFixed(2);
    const label = unlock.label || 'UNLOCKS';
    const note = unlock.note || 'Added to your order with this item';
    const title = `${unlock.title || 'Free gift'}`;

    return `
      <div class="upsell-dialog__unlock">
        <div class="upsell-dialog__unlock-divider">
          <span class="upsell-dialog__unlock-badge">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M13 5.5H3a.5.5 0 0 0-.5.5v1.5h11V6a.5.5 0 0 0-.5-.5Z" fill="currentColor"/>
              <path d="M3 8.5V13a.5.5 0 0 0 .5.5H7.5V8.5H3ZM8.5 13.5H12.5a.5.5 0 0 0 .5-.5V8.5H8.5V13.5Z" fill="currentColor"/>
              <path d="M8 5.5C8 5.5 7.5 2.5 5.75 2.5a1.25 1.25 0 0 0 0 2.5M8 5.5C8 5.5 8.5 2.5 10.25 2.5a1.25 1.25 0 0 1 0 2.5" stroke="currentColor" stroke-width="1" stroke-linecap="round" fill="none"/>
            </svg>
            ${label}
          </span>
        </div>
        <div class="upsell-dialog__unlock-row">
          <div class="upsell-dialog__unlock-product">
            ${unlock.imageUrl ? `
              <div class="upsell-dialog__unlock-image">
                <img src="${unlock.imageUrl}" alt="${unlock.title || ''}" loading="lazy" width="40" height="40">
              </div>
            ` : ''}
            <div class="upsell-dialog__unlock-info">
              <h4 class="upsell-dialog__unlock-title">${title}</h4>
              <p class="upsell-dialog__unlock-note">${note}</p>
            </div>
          </div>
          <div class="upsell-dialog__unlock-price">
            ${compare > 0 ? `
              <span class="upsell-dialog__unlock-price-compare">
                <span class="sr-only">Original price ${compareFormatted}, strikethrough</span>
                <span aria-hidden="true">${compareFormatted}</span>
              </span>
            ` : ''}
            <span class="upsell-dialog__unlock-price-free">FREE</span>
            <span class="upsell-dialog__unlock-added">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ADDED
            </span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Update upsell product quantity
   * @param {string} productId - Product ID
   * @param {number} delta - Change in quantity (+1 or -1)
   */
  updateUpsellQuantity(productId, delta) {
    const product = this.upsellProducts.find(p => String(p.id) === String(productId));
    if (!product) return;

    const maxQty = product.maxQuantity || 5;
    const currentQty = this.upsellQuantities[productId] || 0;
    let newQty = currentQty + delta;

    // Clamp between 0 and max
    newQty = Math.max(0, Math.min(newQty, maxQty));

    // Update state
    if (newQty === 0) {
      delete this.upsellQuantities[productId];
    } else {
      this.upsellQuantities[productId] = newQty;
    }


    // Update card UI
    const card = this.upsellCardsContainer.querySelector(`[data-product-id="${productId}"]`);
    if (card) {
      // Update quantity display
      const qtyDisplay = card.querySelector('.upsell-dialog__qty-display');
      if (qtyDisplay) qtyDisplay.textContent = newQty;

      // Update minus button state
      const minusBtn = card.querySelector('[data-action="decrease"]');
      if (minusBtn) {
        minusBtn.disabled = newQty === 0;
        if (newQty > 0) {
          minusBtn.classList.add(this.classes.minusActiveClass);
        } else {
          minusBtn.classList.remove(this.classes.minusActiveClass);
        }
      }

      // Update plus button state
      const plusBtn = card.querySelector('[data-action="increase"]');
      if (plusBtn) {
        plusBtn.disabled = newQty >= maxQty;
      }

      // Update selected state
      if (newQty > 0) {
        card.classList.add(this.classes.cardSelectedClass);
      } else {
        card.classList.remove(this.classes.cardSelectedClass);
      }
    }

    // Recalculate totals and gift progress
    this.calculateTotals();
    this.updateGiftProgress();

    console.log('[Upsell-Dialog] Updated quantity:', productId, newQty);
  }

  /**
   * Calculate and update footer totals
   */
  calculateTotals() {
    const currentTotal = this.getCurrentTotal();
    const originalTotal = this.getOriginalTotal() + this.getGiftOriginalTotal();

    const currentFormatted = '$' + (currentTotal / 100).toFixed(2);
    const originalFormatted = '$' + (originalTotal / 100).toFixed(2);

    if (this.totalFinalEl) {
      this.totalFinalEl.textContent = currentFormatted;
    }

    if (this.totalCompareEl) {
      if (originalTotal > currentTotal) {
        this.totalCompareEl.textContent = originalFormatted;
        this.totalCompareEl.style.display = '';
      } else {
        this.totalCompareEl.style.display = 'none';
      }
    }
  }

  /**
   * Get selected upsells for cart
   * @returns {Object} Map of productId to { quantity, variantId, price, title, isAddon }
   */
  getSelectedUpsells() {
    const selected = {};

    Object.keys(this.upsellQuantities).forEach(productId => {
      const qty = this.upsellQuantities[productId];
      if (qty > 0) {
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        if (product) {
          const price = this.isSubscription
            ? (product.subscriptionPrice || product.price)
            : (product.onetimePrice || product.price);

          selected[productId] = {
            quantity: qty,
            variantId: product.variantId,
            price: price,
            comparePrice: product.comparePrice || product.price,
            title: product.title,
            isAddon: product.isAddon || false,
            // Bundle/starter-pack upsells carry their component variants so the
            // controller can expand them into multiple line items (mirrors the
            // PDP main-product starter flow). Empty/absent for single-variant upsells.
            bundleName: product.bundleName || '',
            bundleVariants: Array.isArray(product.bundleVariants) ? product.bundleVariants : [],
            // Free "unlocks" attachment (separate product added free with this
            // item). Controller adds it as its own line at promo price 0.
            freeUnlock: product.freeUnlock || null
          };
        }
      }
    });

    return selected;
  }

  /**
   * Trap focus within the dialog for accessibility
   */
  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus the first focusable element
    firstFocusable.focus();

    // Handle tab key to keep focus within dialog
    const handleTabKey = (event) => {
      if (event.key !== 'Tab') return;
      if (!this.classList.contains(this.classes.activeClass)) return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    this.addEventListener('keydown', handleTabKey);
  }
}

// Define the custom element
if (!customElements.get('upsell-dialog')) {
  customElements.define('upsell-dialog', UpsellDialog);
}
